import {
  RoutineExercise,
  RoutineRepetitionExercise,
  RoutineDurationExercise,
  ExerciseOrSuperset,
} from '../../../types/Fisiofit/routine';
import { EXERCISE_TYPE, ExerciseShort } from '../../../types/Fisiofit/exercise';
import { isSuperset } from '../../../utils/supersetUtils';

// Utilidad para detectar índices de ejercicios duplicados en un día
export function getDuplicateExerciseIndexes(exercises: ExerciseOrSuperset[]): number[] {
  const seen: Record<string, number[]> = {};
  exercises.forEach((ex, idx) => {
    if (Array.isArray(ex)) {
      ex.forEach((sup, supIdx) => {
        if (sup.exerciseId) {
          if (!seen[sup.exerciseId]) seen[sup.exerciseId] = [];
          seen[sup.exerciseId].push(`${idx}-superset-${supIdx}` as any);
        }
      });
    } else if (ex.exerciseId) {
      if (!seen[ex.exerciseId]) seen[ex.exerciseId] = [];
      seen[ex.exerciseId].push(idx);
    }
  });
  // Solo nos interesan los índices a partir de la segunda aparición
  const duplicates: number[] = [];
  Object.values(seen).forEach((arr) => {
    if (arr.length > 1) {
      arr.slice(1).forEach((i) => {
        if (typeof i === 'number') duplicates.push(i);
      });
    }
  });
  return duplicates;
}

// Función para encontrar el primer ejercicio con el mismo exerciseId en un día
export function findFirstExerciseWithId(exercises: ExerciseOrSuperset[], targetExerciseId: string): RoutineExercise | null {
  for (const exerciseOrSuperset of exercises) {
    if (isSuperset(exerciseOrSuperset)) {
      for (const exercise of exerciseOrSuperset) {
        if (exercise.exerciseId === targetExerciseId) {
          return exercise;
        }
      }
    } else if (exerciseOrSuperset.exerciseId === targetExerciseId) {
      return exerciseOrSuperset as RoutineExercise;
    }
  }
  return null;
}

// Type guard para verificar si es un ejercicio de repeticiones
export function isRepetitionExercise(exercise: RoutineExercise): exercise is RoutineRepetitionExercise {
  return 'repetitions' in exercise && exercise.repetitions !== undefined;
}

// Type guard para verificar si es un ejercicio de duración
export function isDurationExercise(exercise: RoutineExercise): exercise is RoutineDurationExercise {
  return 'duration' in exercise && exercise.duration !== undefined;
}

// Función para actualizar todos los ejercicios duplicados en un día
export function updateDuplicateExercises(
  exercises: ExerciseOrSuperset[],
  updatedExercise: RoutineExercise,
  sourceIndex: number,
): ExerciseOrSuperset[] {
  return exercises.map((exerciseOrSuperset, index) => {
    if (index === sourceIndex) {
      return updatedExercise;
    }

    if (isSuperset(exerciseOrSuperset)) {
      const updatedSuperset = exerciseOrSuperset.map((exercise) => {
        if (exercise.exerciseId === updatedExercise.exerciseId) {
          return { ...exercise, ...updatedExercise };
        }
        return exercise;
      });
      return updatedSuperset;
    } else if ((exerciseOrSuperset as RoutineExercise).exerciseId === updatedExercise.exerciseId) {
      return { ...exerciseOrSuperset, ...updatedExercise };
    }

    return exerciseOrSuperset;
  });
}

// Función helper para construir los valores del ejercicio
export function buildExerciseValues(
  selectedExercise: ExerciseShort,
  preloadValues: any,
  existingExercise?: RoutineExercise | null,
) {
  const baseValues = {
    exerciseId: selectedExercise.id,
    name: selectedExercise.name,
    exerciseType: selectedExercise.exerciseType,
    imageUrl: selectedExercise.imageUrl,
    videoUrl: selectedExercise.videoUrl,
  };

  if (existingExercise) {
    // Usar valores del ejercicio existente
    return {
      ...baseValues,
      sets: existingExercise.sets,
      weight: existingExercise.weight,
      rir: existingExercise.rir,
      rpe: existingExercise.rpe,
      comment: existingExercise.comment,
      ...(selectedExercise.exerciseType === EXERCISE_TYPE.TIME
        ? {
            repetitions: undefined,
            duration: isDurationExercise(existingExercise) ? existingExercise.duration : undefined,
          }
        : {}),
      ...(selectedExercise.exerciseType === EXERCISE_TYPE.REPS
        ? {
            duration: undefined,
            repetitions: isRepetitionExercise(existingExercise) ? existingExercise.repetitions : undefined,
          }
        : {}),
    };
  }
  // Usar valores de preload o valores por defecto
  return {
    ...baseValues,
    ...(selectedExercise.exerciseType === EXERCISE_TYPE.TIME
      ? {
          repetitions: undefined,
          duration: preloadValues && 'duration' in preloadValues ? preloadValues.duration : undefined,
        }
      : {}),
    ...(selectedExercise.exerciseType === EXERCISE_TYPE.REPS
      ? {
          duration: undefined,
          repetitions: preloadValues && 'repetitions' in preloadValues ? preloadValues.repetitions : undefined,
        }
      : {}),
    weight: preloadValues?.weight,
    rir: preloadValues?.rir,
    rpe: preloadValues?.rpe,
    sets: preloadValues?.sets,
  };
}
